<template lang="pug">
  div(v-if="fbLoaded")
    slide-drawer(
        v-model="showFacebookDrawer"
        :defaultCancel="step !== 'success'"
      )
      template(slot="title")
        span Connect to Facebook Ad Account
      template(slot="content")
        v-container.drawer-container
          v-flex
            v-layout.align-center.my-3.px-5
              v-flex.text-xs-center
                img.logo(src='../../assets/images/liondesk-marketing-logo.png')
              v-flex.text-xs-center
                v-icon.facebook-sync(color="primary") repeat
              v-flex.text-xs-center
                v-icon.facebook-logo(color="primary") $vuetify.icons.facebook
                
            v-layout(v-if="step === 'loading'" align-center column)
              .text-xs-center Loading...
            v-layout(v-else-if="step === 'start'" align-center column)
              v-flex.mb-5 
                p.ml-4 Connect to your Facebook Business ad accounts to 
                  | start creating custom audiences based on your contacts.
                p.ml-4.mt-3 Then, target your new audiences with relevant ads for any stage 
                    | in your LionDesk journey.
              v-flex
                v-btn(color="primary" @click="connectToFacebook") Connect to Facebook
            v-layout(v-else-if="step === 'no-access'" column)
              v-flex.mb-5 
                p.ml-4 You need to grant the necessary permissions to start 
                  | creating custom audiences based on your contacts.
              v-flex.text-xs-center
                v-btn(color="primary" @click="connectToFacebook") Connect to Facebook
            v-layout(v-else-if="step === 'adding-accounts'" column)
              v-flex.ml-4
                span Select the ad accounts you want to connect to LionDesk:
              v-flex.ml-4
                v-checkbox(
                  v-model="selectedAdAccounts"
                  :id="account.account_id"
                  :value="account"
                  :disabled="isInFbPersonalAdAccounts(account.account_id)"
                  v-for="account in fb_adAccounts"
                )
                  template(slot="label")
                    span.black--text {{`${account.name} (${account.account_id})`}}
            v-layout(v-else-if="step === 'success'" column)
              v-flex.text-xs-center
                h2 All done!
                span Your ad accounts have been connected to LionDesk.
            v-layout(v-else-if="step === 'no-accounts'" column)
              v-flex
                span.mb-5 We couldn’t find any ad accounts for custom audience creation. 
                  | Create an ad account using Facebook Business Manager or convert 
                  | your personal ad account to create audiences with LionDesk.
              v-flex.text-xs-center
                v-btn(color="primary" href="https://www.facebook.com/business/help/910137316041095?id=420299598837059" target="_blank")
                  v-icon(left) launch
                  | Create Ad Account
          v-flex.mt-2.ml-4.disconnect-fb-account-container(v-if="step === 'no-accounts' || step === 'adding-accounts'")
            disconnect-facebook-account(
              @confirm-disconnect-fb-account="handleDeactivateFbUser"
            )
              a.font-weight-bold.accent--text(slot="activator" @click="")
                | Disconnect my Facebook account
      template(slot="actions")
        v-btn(
          color="primary"
          v-if="step === 'adding-accounts'"
          :disabled="!isFormValid"
          :loading="isSavingAdAccounts"
          @click="saveAdAccounts"
        ) Save
        v-btn(color="primary" v-if="step === 'success'" @click="showFacebookDrawer = false") Ok
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex';
import SlideDrawer from '@/Core/components/SlideDrawer';
import toggleSlideDrawer from '@/Core/mixins/toggle-slide-drawer';
import facebookMixin from '@/Core/mixins/facebook-sdk';
import { FB_SCOPE_LOGIN, FB_RETURN_SCOPES } from '@/Core/config/facebook';
import facebookTypes from '@/Contacts/config/facebook-source-types';
import DisconnectFacebookAccount from '@/Core/components/marketing/DisconnectFacebookAccount'

const steps = {
  loading: 'loading',
  start: 'start',
  no_access: 'no-access',
  no_accounts: 'no-accounts',
  adding_accounts: 'adding-accounts',
  success: 'success',
};

export default {
  components: {
    SlideDrawer,
    DisconnectFacebookAccount,
  },
  mixins: [toggleSlideDrawer, facebookMixin],
  props: {
    value: Boolean,
  },
  data() {
    return {
      selectedAdAccounts: [],
      step: steps.loading,
    };
  },
  computed: {
    ...mapGetters('facebookBusiness', ['isPersonalFbConnected', 'hasAdsManagementPermission']),
    ...mapState({
      isSavingAdAccounts: state => state.facebookBusiness.isSavingAdAccounts,
      fbPersonalAdAccounts: state => state.facebookBusiness.fbPersonalAdAccounts
    }),
    isFormValid() {
      return this.selectedAdAccounts.length && this.isSavingAdAccounts === false && this.hasNewValues();
    },
    showFacebookDrawer: {
      get() { return this.value; },
      set(value) {
        if (!value) this.resetState();
        this.$emit('input', value);
      },
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.getPersonalFbData();
      }
    },
  },
  methods: {
    ...mapActions('facebookBusiness', ['getFacebookPermissions', 'getFbAdAccount', 'deactivateFbUser']),
    ...mapMutations('facebookBusiness', ['setIsSavingAdAccounts', 'setFbAccountType']),
    connectToFacebook() {
      window.FB.login(resp => {
        if (resp.status === 'connected') {
          this.handleFbConnected(resp);
        }
      },  { ...FB_SCOPE_LOGIN, ...FB_RETURN_SCOPES });
    },
    handleFbConnected(response) {
      const {
        accessToken,
        userID: fbUserId,
        grantedScopes = '',
      } = response.authResponse;

      if (grantedScopes && grantedScopes.includes('ads_management')) {
        this.saveFbUser(fbUserId, accessToken);
      } else {
        this.step = steps.no_access;
      }
    },
    async saveFbUser(fbUserId, accessToken) {
      const response = await this.fb_createFbUser(fbUserId, accessToken);
      this.$emit('successfully-connected');
      if (!response.errors) {
        this.getAdAccounts();
      }
    },
    async saveAdAccounts() {
      this.setIsSavingAdAccounts(true);
      await this.fb_saveAdAccountsConnected(this.selectedAdAccounts);
      this.step = steps.success;
      this.setIsSavingAdAccounts(false);
      this.$emit('successfully-connected');
    },
    async getAdAccounts(fbUserId) {
      this.setFbAccountType(facebookTypes.fbPersonal.type);
      await this.getFbAdAccount();
      await this.fb_getFbUserAdAccounts();
      if (this.fbPersonalAdAccounts.length > 0) {
        this.selectedAdAccounts = this.fb_adAccounts.filter(adAccount => this.isInFbPersonalAdAccounts(adAccount.account_id));
      }
      this.step = this.fb_adAccounts.length
        ? steps.adding_accounts
        : steps.no_accounts;
    },
    isInFbPersonalAdAccounts(adAccountId) {
      return this.fbPersonalAdAccounts.some(personalAdAccount => personalAdAccount.fb_ad_account_id === adAccountId);
    },
    resetState() {
      this.step = steps.loading;
      this.selectedAdAccounts = [];
      this.fb_adAccounts = [];
    },
    async getPersonalFbData() {
      this.setFbAccountType(facebookTypes.fbPersonal.type);
      const fbUser = {
        id: facebookTypes.fbPersonal.id,
        type: facebookTypes.fbPersonal.type,
      };
      await this.getFacebookPermissions(fbUser);
      if (!this.isPersonalFbConnected) {
        this.step = steps.start;
        return;
      }
      if (!this.hasAdsManagementPermission) {
        this.step = steps.no_access;
        return;
      }
      this.getAdAccounts();
    },
    hasNewValues() {
      return this.selectedAdAccounts.some(adAccount => !this.isInFbPersonalAdAccounts(adAccount.account_id));
    },
    async handleDeactivateFbUser() {
      await this.deactivateFbUser();
      this.resetState();
      this.getPersonalFbData();
      this.$emit('successfully-disconnected');
      
    }
  },
};
</script>
<style scoped lang="scss">
img.logo {
  width: 110px;
  &:after {
    content: 'TM';
    position: absolute;
    font-size: 5px;
    width: 10px;
    height: 10px;
    color: var(--v-primary-base);
    top: 14px;
    left: 50px;
  }
}
i.v-icon.facebook-sync {
  font-size: 64px;
}
svg.v-icon.facebook-logo {
  font-size: 120px;
  height: 120px;
}

.drawer-container {
  display: flex; 
  flex-flow: column; 
  height:100%; 
  padding-bottom: 10px;
}

.disconnect-fb-account-container {
  flex: none;
}
</style>
